import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Clock, X } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/lib/AuthContext";
import {
  useActiveWorkout,
  formatElapsedShort,
} from "@/lib/ActiveWorkoutContext";
import { base44 } from "@/api/base44Client";
import ActiveWorkoutDialog from "./ActiveWorkoutDialog";

/**
 * Pill button that displays the elapsed-time timer while a workout is in
 * progress. Tap → opens the ActiveWorkoutDialog. Returns `null` when no
 * workout is active so callers can simply unconditionally render it next to
 * the regular account avatar / membership chip.
 */
export default function WorkoutTimerPill({ size = "md", className = "" }) {
  const { active, elapsedSeconds, endWorkout } = useActiveWorkout();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const todayDate = format(new Date(), "yyyy-MM-dd");

  // Read today's exercise completions so the modal can show "X of Y" and the
  // pill can keep the calorie estimate accurate. This is the same query
  // TodaysWorkout uses, so React Query de-dupes.
  const { data: exerciseCompletions = [] } = useQuery({
    queryKey: ["exercise-completions", todayDate],
    queryFn: () =>
      base44.entities.ExerciseCompletion.filter({
        user_id: user.id,
        completed_date: todayDate,
      }),
    enabled: !!user && !!active,
  });

  if (!active) return null;

  const completedCount = active
    ? exerciseCompletions.filter((c) => c.workout_plan_id === active.plan_id)
        .length
    : 0;

  const sizeClasses =
    size === "sm"
      ? "px-2.5 py-1 text-[11px]"
      : "px-3 py-1.5 text-xs";

  return (
    <>
      <div className="inline-flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="workout-timer-pill"
          className={`inline-flex items-center gap-1.5 rounded-full font-mono font-bold tracking-tight bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/20 active:scale-95 transition ${sizeClasses} ${className}`}
          title="View active workout"
        >
          <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-400" />
          <span>{formatElapsedShort(elapsedSeconds)}</span>
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            endWorkout();
          }}
          data-testid="workout-timer-quick-stop"
          className="w-5 h-5 sm:w-5.5 sm:h-5.5 rounded-full bg-white/5 hover:bg-rose-500/20 border border-white/10 hover:border-rose-400/40 text-white/40 hover:text-rose-300 flex items-center justify-center transition"
          title="Stop & discard timer"
          aria-label="Stop workout timer"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      <ActiveWorkoutDialog
        open={open}
        onOpenChange={setOpen}
        exercisesCompleted={completedCount}
      />
    </>
  );
}
